import type { TranslationKeys } from '../../../i18n';
import { UserMembershipDiscountType } from '../../../models/user-membership/dto/user-membership.dto';

/**
 * MYS-122 (B1b-i, generalised at H16) — client-side guard for EVERY membership
 * discount type/value pair: event joining, coaching sessions, court booking,
 * and the per-time-window court pair.
 *
 * WHY THIS EXISTS. The API's real guard is `normalizeMembershipDiscount`
 * (api/src/user-membership/service/membership-type/normalize-sports.ts) and it
 * **fails safe**: anything it rejects is stored as "no discount" rather than
 * throwing. The global pipe is `SoftValidationPipe` in soft mode, so the
 * advisory `@Min(0)` / `@IsEnum` decorators on the DTO don't reject either.
 * Net effect without this file: an admin types `150`, the request returns 200,
 * the dashboard shows a success toast — and the discount was silently zeroed.
 *
 * So this mirrors the server's accept-set in order to *report* instead of
 * silently discarding. Keep the two in lockstep; the server is authoritative
 * and this is only ever allowed to be stricter, never looser.
 *
 * H16 note: before the server guard existed, the coaching and court inputs
 * read their value with `parseInt(e.target.value, 10)`, so CLEARING one
 * produced `NaN` — and `JSON.stringify(NaN)` is `null`, which the `int not
 * null` column rejected as a 500 rather than a validation message. Those
 * inputs now read the way the event one already did, and this validator turns
 * the cleared state into a blocked submit.
 *
 * UNITS. `PERCENTAGE` is a whole percent (0-100). `CONSTANT` is whole MAJOR
 * currency units (euros) — never cents. The stored column is an `int` and the
 * server floors, which is why a fractional value is rejected here rather than
 * quietly truncated.
 */

/** Mirrors MAX_PERCENTAGE_DISCOUNT in the API normalizer. */
export const MAX_EVENT_JOINING_PERCENTAGE_DISCOUNT = 100;

export type MembershipDiscountError =
  | 'invalid_type'
  | 'not_a_whole_number'
  | 'negative'
  | 'percentage_over_max';

/**
 * i18n key per failure, so the caller renders a specific message. Typed as
 * `TranslationKeys` (a type-only import — nothing from i18n is pulled in at
 * runtime) so the compiler proves every key below actually exists in en.ts.
 */
export const EVENT_JOINING_DISCOUNT_ERROR_KEYS: Record<
  MembershipDiscountError,
  TranslationKeys
> = {
  invalid_type: 'userMembership.eventJoiningDiscountType_invalid',
  not_a_whole_number: 'userMembership.eventJoiningDiscountValue_invalid_integer',
  negative: 'userMembership.eventJoiningDiscountValue_invalid_negative',
  percentage_over_max:
    'userMembership.eventJoiningDiscountValue_invalid_percentage',
};

/**
 * H16 — field-agnostic wording, for the coaching and court pairs.
 *
 * The event pair keeps its own map above rather than being folded in here: its
 * English strings name the field ("Event Joining Discount must be a whole
 * number") and they are shipped, translated and asserted by
 * `AddUserMembershipTypeModal.eventDiscount.test.tsx`. Rewording them to share
 * one map would change reviewed copy for no functional gain.
 */
export const MEMBERSHIP_DISCOUNT_ERROR_KEYS: Record<
  MembershipDiscountError,
  TranslationKeys
> = {
  invalid_type: 'userMembership.discountType_invalid',
  not_a_whole_number: 'userMembership.discountValue_invalid_integer',
  negative: 'userMembership.discountValue_invalid_negative',
  percentage_over_max: 'userMembership.discountValue_invalid_percentage',
};

/**
 * Returns the reason the pair would be rejected (or silently zeroed) by the
 * API, or `undefined` when it is safe to submit.
 */
export function validateMembershipDiscount(
  discountType: UserMembershipDiscountType | undefined | null,
  discountValue: number | undefined | null,
): MembershipDiscountError | undefined {
  if (
    discountType !== UserMembershipDiscountType.PERCENTAGE &&
    discountType !== UserMembershipDiscountType.CONSTANT
  ) {
    return 'invalid_type';
  }

  // Covers undefined/null (a cleared input), NaN (an unparseable one) and
  // Infinity, all of which the server reads as "not a number" and zeroes.
  if (typeof discountValue !== 'number' || !Number.isFinite(discountValue)) {
    return 'not_a_whole_number';
  }

  // Checked before integer-ness so `-5` reports the more useful reason;
  // `-5.5` is both, and reporting "negative" first is still accurate.
  if (discountValue < 0) {
    return 'negative';
  }

  if (!Number.isInteger(discountValue)) {
    return 'not_a_whole_number';
  }

  if (
    discountType === UserMembershipDiscountType.PERCENTAGE &&
    discountValue > MAX_EVENT_JOINING_PERCENTAGE_DISCOUNT
  ) {
    return 'percentage_over_max';
  }

  return undefined;
}
